<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="px-6 pb-6 sm:rounded-lg sm:px-12">
        <p class="mx-auto mb-10 flex justify-center text-xl font-semibold">
          {{ t("msg.twoFactorAuth") }}
        </p>
        <form class="space-y-2" @submit.prevent="handleLogin">
          <RateLimitCountdown
            v-if="rateLimitSeconds > 0"
            :seconds="rateLimitSeconds"
            :message="t('msg.tooManyIncorrectCodes')"
            @expired="rateLimitSeconds = 0"
          />
          <p v-if="errorMessage && rateLimitSeconds === 0" class="text-danger">
            {{ t("msg.error") }}: {{ errorMessage }}
          </p>
          <Input
            ref="codeInputEl"
            type="text"
            model-label="msg.code"
            model-name="Code"
            :model-value="codeInput"
            :color="`${theme}${highContrast ? '-hc' : ''}`"
            :aria-label="t('msg.code')"
            @update:model-value="(c) => (codeInput = c)"
            autocomplete-hint="one-time-code"
            inputmode="numeric"
            pattern="[0-9]*"
            :maxlength="6"
          />

          <div>
            <button
              type="submit"
              :disabled="isSubmitting"
              :class="globals.btnClass(theme, highContrast)"
              class="mt-6 flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <font-awesome-icon
                v-if="isSubmitting"
                icon="fa-sharp-duotone fa-solid fa-spinner-third"
                spin
                class="mr-2"
              />
              {{ t("msg.login") }}
            </button>
          </div>
          <button type="button" class="cursor-pointer text-white" @click="logout">
            {{ t("msg.backToLogin") }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import authService from "../services/auth.service.ts";
import Input from "../components/Input.vue";
import RateLimitCountdown from "../components/RateLimitCountdown.vue";
import globals from "../globals";
import { useColorMode } from "@vueuse/core";

import { useI18n } from "vue-i18n";
import WordmarkLogo from "../components/WordmarkLogo.vue";
import { useAuthStore } from "../pinia/auth.ts";
import { useThemeStore } from "../pinia/theme.ts";
import { performLogout } from "../utils/logoutRedirect";
import { postLoginNavigate } from "../utils/postLoginNavigate";

const { t } = useI18n();

const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const router = useRouter();
const route = useRoute();

const theme = computed(() => themeStore.accent);
const highContrast = computed(() => themeStore.highAccessibility);
const errorMessage = ref("");
const codeInput = ref(null);
const codeInputEl = ref(null);
const rateLimitSeconds = ref(0);
const isSubmitting = ref(false);

onMounted(() => codeInputEl.value?.focus());

useColorMode({
  initialValue: "dark",
});

const handleLogin = async () => {
  // Don't submit if rate limited
  if (rateLimitSeconds.value > 0) {
    return;
  }

  isSubmitting.value = true;

  try {
    await authService.twoFactorVerify({ Code: codeInput.value });
    const status = await authStore.fetchStatus();

    await postLoginNavigate(route, router);
  } catch (error) {
    // Check for rate limiting (429 status)
    if (error.response?.status === 429) {
      const retryAfter = parseInt(error.response.headers["retry-after"] || "30", 10);
      rateLimitSeconds.value = retryAfter;
      errorMessage.value = "";
    } else {
      errorMessage.value = globals.renderError(t, error);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const logout = async () => {
  await performLogout();
};
</script>
